import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById, createUser, updateUser, getGroups, getAssetUrl } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import Select from "react-select";
import PageHeader from "../components/UI/PageHeader";

export default function AddEditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // الحالات (States) - أضفنا gender هنا
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    phone: "",
    jobTitle: "",
    gender: "male", // افتراضي ذكر
    isActive: true
  });
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  // Same fix as Profile.js's "Reset Photo": clearing the local preview alone
  // never tells the backend anything (handleSubmit only appends "avatar"
  // when a new file was picked), so the old avatar_url just comes back
  // after saving. This flag makes Reset Photo explicit.
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getGroups(token).then(res => setGroups(res.map(g => ({ value: g.id, label: g.name }))));
    
    if (id) {
      getUserById(token, id).then(u => {
        setFormData({
          username: u.username,
          email: u.email,
          firstName: u.first_name || "",
          lastName: u.last_name || "",
          phone: u.phone || "",
          jobTitle: u.job_title || "",
          gender: u.gender || "male", // جلب النوع من الداتابيز
          isActive: u.is_active ?? true,
          password: ""
        });
        setSelectedGroups(u.groups.map(g => ({ value: g.id, label: g.name })));
        if (u.avatar_url) setAvatarPreview(u.avatar_url);
      });
    }
  }, [id, token]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setRemoveAvatar(false);
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    let tempErrors = {};

    if (!formData.username.trim()) tempErrors.username = "Username is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.email.trim()) {
        tempErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
        tempErrors.email = "Invalid email format";
    }
    if (!id && !formData.password) tempErrors.password = "Password is required for new users";

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      notify.error("Please fix the errors below");
      return;
    }

    setLoading(true);
    
    const formDataToSend = new FormData();
    formDataToSend.append("username", formData.username);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("firstName", formData.firstName);
    formDataToSend.append("lastName", formData.lastName);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("jobTitle", formData.jobTitle);
    formDataToSend.append("gender", formData.gender); // إرسال النوع
    formDataToSend.append("isActive", String(formData.isActive));
    
    if (formData.password) formDataToSend.append("password", formData.password);
    
    const groupIds = selectedGroups.map(g => g.value).join(",");
    formDataToSend.append("groups", groupIds);

    if (avatarFile) {
      formDataToSend.append("avatar", avatarFile);
    } else if (removeAvatar) {
      formDataToSend.append("removeAvatar", "true");
    }

    try {
      if (id) {
        await updateUser(token, id, formDataToSend);
        notify.success("User updated successfully");
      } else {
        await createUser(token, formDataToSend);
        notify.success("User created successfully");
      }
      navigate("/users");
    } catch (err) {
      const errorMsg = err.data?.error || "Error saving data";
      const lowMsg = errorMsg.toLowerCase();
      let serverErrors = {};
      if (lowMsg.includes("username")) serverErrors.username = errorMsg;
      if (lowMsg.includes("email")) serverErrors.email = errorMsg;
      if (lowMsg.includes("phone")) serverErrors.phone = errorMsg;
      if (Object.keys(serverErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...serverErrors }));
      }
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '12px',
      padding: '4px',
      borderColor: '#f3f4f6',
      backgroundColor: '#f9fafb80',
      '&:hover': { borderColor: 'var(--primary-color)' },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'var(--primary-color)',
      color: 'white',
      borderRadius: '8px',
    }),
    multiValueLabel: (base) => ({ ...base, color: 'white' }),
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/users")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div>
            <h2 className="text-2xl font-black text-gray-800 tracking-tight">
              {id ? "Edit User Record" : "Register New User"}
            </h2>
            <p className="text-sm text-gray-400 font-medium italic">Internal System Access Management</p>
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col items-center">
            <div className="relative mb-6">
              <div className="w-40 h-40 rounded-[3rem] overflow-hidden ring-8 ring-gray-50 bg-gray-100 flex items-center justify-center transition-all hover:ring-[var(--primary-color)]/10">
                {avatarPreview ? (
                  <img 
                    src={getAssetUrl(avatarPreview)}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <img 
                    src={getAssetUrl(formData.gender === "female" ? "/img/avatars/Female-2.png" : "/img/avatars/Male.jpg")}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                )}
              </div>
              <label className="absolute bottom-2 right-2 bg-[var(--primary-color)] text-white p-3 rounded-2xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                <ICONS.Plus className="w-5 h-5" />
                <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
              </label>
            </div>

            {/* قسم اختيار النوع بالأيقونات */}
            <div className="flex gap-4 mb-6 bg-gray-50 p-2 rounded-2xl border border-gray-100">
              <button 
                type="button"
                onClick={() => setFormData({...formData, gender: 'male'})}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${formData.gender === 'male' ? 'bg-white shadow-sm text-blue-500 font-bold' : 'text-gray-400'}`}
              >
                <span className="text-lg">♂</span> Male
              </button>
              <button 
                type="button"
                onClick={() => setFormData({...formData, gender: 'female'})}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${formData.gender === 'female' ? 'bg-white shadow-sm text-pink-500 font-bold' : 'text-gray-400'}`}
              >
                <span className="text-lg">♀</span> Female
              </button>
            </div>

            <h3 className="font-black text-gray-700 uppercase text-xs tracking-widest">Profile Picture</h3>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed font-medium">PNG, JPG or GIF. Max 2MB.</p>
            
            {avatarPreview && (
              <button 
                onClick={() => {setAvatarFile(null); setAvatarPreview(null); setRemoveAvatar(true);}}
                className="mt-4 text-[10px] font-black uppercase text-red-400 hover:text-red-600 transition-colors"
              >
                Reset Photo
              </button>
            )}

            <div className="w-full space-y-4 mt-4">
              <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Account Status</span>
                  <input 
                    type="checkbox" 
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    className="w-5 h-5 accent-[var(--primary-color)]"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-gray-200"></span>
              Core Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FloatingInput
                label="First Name"
                value={formData.firstName}
                onChange={e => setFormData({...formData, firstName: e.target.value})}
                icon={ICONS.EditUser}
              />
              <FloatingInput
                label="Last Name"
                value={formData.lastName}
                onChange={e => setFormData({...formData, lastName: e.target.value})}
                icon={ICONS.EditUser}
              />
              <FloatingInput
                label="Login Username"
                value={formData.username}
                onChange={e => {
                  setFormData({...formData, username: e.target.value});
                  if(errors.username) setErrors(prev => ({...prev, username: ""}));
                }}
                error={errors.username}
                icon={ICONS.EditUser}
              />
              <FloatingInput
                label="Work Email"
                type="email"
                value={formData.email}
                onChange={e => {
                  setFormData({...formData, email: e.target.value});
                  if(errors.email) setErrors(prev => ({...prev, email: ""}));
                }}
                error={errors.email}
                icon={ICONS.Email}
              />
              <FloatingInput
                label="Job Title"
                value={formData.jobTitle}
                onChange={e => setFormData({...formData, jobTitle: e.target.value})}
                icon={ICONS.Plus}
              />
              <FloatingInput
                label="Phone Number"
                value={formData.phone}
                onChange={e => {
                  setFormData({...formData, phone: e.target.value});
                  if(errors.phone) setErrors(prev => ({...prev, phone: ""}));
                }}
                error={errors.phone}
                icon={ICONS.Plus}
              />
              <p className="text-[11px] text-gray-400 md:col-span-2 -mt-4">
                Username, Email, and Phone (if set) can all be used to log in.
              </p>
            </div>

            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] my-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-gray-200"></span>
              Security & Access
            </h3>

            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ms-4">
                  Access Permissions Groups
                </label>
                <Select 
                  options={groups} 
                  value={selectedGroups} 
                  onChange={setSelectedGroups} 
                  isMulti 
                  styles={selectStyles}
                  placeholder="Assign to security groups..."
                />
              </div>

              <FloatingInput 
                label={id ? "Change Password (Leave blank to skip)" : "Account Password"} 
                type="password"
                value={formData.password} 
                onChange={e => {
                  setFormData({...formData, password: e.target.value});
                  if(errors.password) setErrors(prev => ({...prev, password: ""}));
                }}
                error={errors.password}
                icon={ICONS.Lock}
              />
            </div>

            <div className="flex gap-4 mt-12 pt-8 border-t border-gray-50">
              <Button 
                variant="primary" 
                className="flex-grow shadow-lg shadow-blue-500/20" 
                icon={id ? ICONS.Save : ICONS.Plus} 
                onClick={handleSubmit}
                loading={loading}
              >
                {id ? "Save User Changes" : "Register User"}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/users")}>Cancel</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}